import { z } from 'zod';
import { setMaintenance } from './maintenance';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

let token: string | null = localStorage.getItem('straddle_token');

export function setToken(t: string | null) {
  token = t;
  if (t) localStorage.setItem('straddle_token', t);
  else localStorage.removeItem('straddle_token');
}

export function getToken(): string | null {
  return token;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Resolve a server-provided media path to an absolute URL the browser can load.
 *  Absolute URLs (Telegram avatars) pass through; "/api/..." paths get the API
 *  base prefixed so they work cross-origin in dev. */
export function mediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//.test(url)) return url;
  return `${BASE}${url}`;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
  body?: unknown;
  /** Raw body (e.g. FormData); when set, Content-Type is left to the browser. */
  formData?: FormData;
  auth?: boolean;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (!opts.formData) headers['Content-Type'] = 'application/json';
  if (opts.auth !== false && token) headers.Authorization = `Bearer ${token}`;
  // Skip ngrok's free-tier interstitial on /api fetches (harmless otherwise).
  headers['ngrok-skip-browser-warning'] = 'true';

  const res = await fetch(`${BASE}/api${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.formData ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      // Maintenance: any 503 from a normal request flips the app into the stub.
      if (res.status === 503 && data?.maintenance) setMaintenance(true, data.message ?? null);
      if (data?.message) message = Array.isArray(data.message) ? data.message.join(', ') : data.message;
    } catch {
      /* ignore non-JSON error bodies */
    }
    throw new ApiError(res.status, message);
  }

  const json = await res.json();
  return schema.parse(json);
}

export const api = {
  get: <T>(path: string, schema: z.ZodType<T>) => request(path, schema),
  post: <T>(path: string, schema: z.ZodType<T>, body?: unknown) =>
    request(path, schema, { method: 'POST', body, auth: !path.startsWith('/auth/') }),
  patch: <T>(path: string, schema: z.ZodType<T>, body?: unknown) =>
    request(path, schema, { method: 'PATCH', body }),
  put: <T>(path: string, schema: z.ZodType<T>, body?: unknown) =>
    request(path, schema, { method: 'PUT', body }),
  del: <T>(path: string, schema: z.ZodType<T>) => request(path, schema, { method: 'DELETE' }),
  upload: <T>(path: string, schema: z.ZodType<T>, formData: FormData) =>
    request(path, schema, { method: 'POST', formData }),
};
